import React, { type FC, useMemo, useRef, useState } from "react";
import ContentAssignmentModalTwoSteps from "components/assignmentModals/contentAssignmentModal/contentAssignmentModalTwoSteps/ContentAssignmentModalTwoSteps";
import { bindAction, type PacksContextItem } from "../../../../../../../interfaces";
import { ContentType } from "../../../../../PeopleAssignments/types";
import ThreatDefenceContentTypes from "../../../../../../../enums/threatDefenceContentTypes";
import { campaignsStateSelector } from "../../../state/selectors";
import { type AppDispatch, type RootState } from "../../../../../../Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { reset } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/packsContextSlice";
import PaymentModal from "../../../../../../Licensing/Packs/PaymentModal/PaymentModal";
import type { LicenseConfirmationFormRef } from "../../../../../../Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import useToggle from "../../../../../../../hooks/useToggle";
import { useFeatureFlag } from "../../../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../../../featureFlags";
import { updateLicensePackInfo } from "../../../../../../Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";

interface Props {
  campaignId?: string;
  modalVisible: boolean;
  showModal: (visible: boolean) => void;
  createCampaignSend: (packIds: number[]) => void;
  setIsProcessing: (processing: boolean) => void;
}
export const LicenseConfirmationContainer: FC<Props & PropsFromRedux> = (props) => {
  const { campaignTitle, flowId, flowThumbnailUrl, flowTitle, campaignId } = props;
  const [showPaymentModal, togglePaymentModal] = useToggle(false);
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const [selectedPackIds, setSelectedPackIds] = useState<number[]>([]);
  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({
    getValues: () => [],
  });

  const confirmLicense = () => {
    const packIds = props.availableLicenses.flatMap((item) => item.packId);
    if (additionalPurchaseEnabled && props.isPurchaseNeeded) {
      props.updateLicensePackInfo(licenseConfirmationFormRef.current.getValues());
      setSelectedPackIds(packIds);
      togglePaymentModal();
    } else {
      createCampaignSendHandler(packIds);
    }
    props.showModal(false);
  };

  const createCampaignSendHandler = (packs: number[]) => {
    props.createCampaignSend(packs);
    props.resetLicensingModal();
  };

  const onPaymentSuccessHandler = () => {
    togglePaymentModal();
    createCampaignSendHandler(selectedPackIds);
  };

  const cancelLicenseModal = () => {
    props.showModal(false);
    props.setIsProcessing(false);
    props.resetLicensingModal();
  };

  const packContextItems: PacksContextItem[] = useMemo(
    () => [
      { id: flowId?.toString() || "", type: ContentType.Flow, title: flowTitle || "", thumbnailUrl: flowThumbnailUrl },
      { id: campaignId || "", type: ThreatDefenceContentTypes.Campaign, title: campaignTitle },
    ],
    [campaignTitle, flowId, flowThumbnailUrl, flowTitle, campaignId],
  );

  return (
    <>
      <ContentAssignmentModalTwoSteps
        skipNotifyStep
        notifyStepDisabled
        skipLicensing={false}
        flowHasOrigin={false}
        showModal={props.modalVisible}
        onCancel={cancelLicenseModal}
        onConfirm={confirmLicense}
        packContextItems={packContextItems}
        selectedUserIds={props.selectedUserIds}
        selectedGroupIds={props.selectedGroupIds}
        licenseConfirmationFormRef={licenseConfirmationFormRef}
      />
      {showPaymentModal && props.paymentPacks && (
        <PaymentModal
          paymentPacks={props.paymentPacks}
          onClose={() => {
            cancelLicenseModal();
            togglePaymentModal();
          }}
          onPaymentSuccess={onPaymentSuccessHandler}
        />
      )}
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
    campaignTitle: base.details.campaign.name,
    selectedUserIds: base.sends.people.assignedUsers.userIds,
    selectedGroupIds: base.sends.people.assignedGroups.groupIds,
    flowId: base.details.campaign.remedialTraining.flowId,
    flowTitle: base.details.campaign.remedialTraining.flowTitle,
    flowThumbnailUrl: base.details.campaign.remedialTraining.flowThumbnailUrl,
    isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
    availableLicenses: state.licensing?.availableLicenses?.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  resetLicensingModal: bindAction(reset, dispatch),
  updateLicensePackInfo: bindAction(updateLicensePackInfo, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(LicenseConfirmationContainer);
export default ConnectedComponent;
